import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Button,
  Avatar,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleIcon from "@mui/icons-material/People";

import { useContacts } from "../../hooks/crm/useContacts";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { useAuthStore } from "../../stores/authStore";
import { useCreateFamilyWithMembers } from "../../hooks/crm/useFamilies";
import {
  FamilyMembershipType,
} from "../../interfaces/DTO/crm/family";

import { formatName } from "../../utils/namesFormater";
import { useIdStore, useViewedTypeStore } from "../../stores/selectedToViewStore";

// ✅ פונקציה חדשה להצעת שם משפחה
const computeDefaultFamilyNameFromContacts = (contacts: Contact[]): string => {
  const lastNames = contacts
    .map((c) => c.last_name?.trim())
    .filter((name): name is string => !!name);

  const uniqueLastNames = [...new Set(lastNames)];

  if (uniqueLastNames.length === 1) {
    return uniqueLastNames[0];
  }

  if (uniqueLastNames.length > 1) {
    return uniqueLastNames.join("-");
  }

  const firstNames = contacts
    .map((c) => c.first_name?.trim())
    .filter((name): name is string => !!name);

  return firstNames.slice(0, 2).join("-");
};

interface AddNewFamilyProps {
  onClose: () => void;
  initialContributors?: Contact[];
}

// the "add dependents" stage is hidden for now - set to true to bring it back
const SHOW_DEPENDENTS_STAGE = false;

export const AddNewFamily: React.FC<AddNewFamilyProps> = ({ onClose, initialContributors }) => {
  const { data: contacts, isLoading, error } = useContacts();
  const [contributors, setContributors] = useState<Contact[]>(initialContributors || []);
  const [dependents, setDependents] = useState<Contact[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [familyName, setFamilyName] = useState<string>("");
  const { mutateAsync: createFamilyWithMembers } = useCreateFamilyWithMembers();
  const [stage, setStage] = useState<"contributors" | "dependents" | "family">("contributors");
  const setViewedType = useViewedTypeStore((state) => state.setViewedType);
  const setId = useIdStore((state) => state.setId);

    useEffect(() => {
    if (initialContributors &&initialContributors.length > 0) {
      setContributors(initialContributors);
      setStage("family"); // דילוג ישר לשם משפחה
    }
  }, [initialContributors]);
  useEffect(() => {
    if (stage === "family") {
      setFamilyName(computeDefaultFamilyNameFromContacts(contributors));
    }
  }, [stage, contributors]);



  const handleContactSelect = (
    contact: Contact,
    list: Contact[],
    setList: (contacts: Contact[]) => void
  ) => {
    if (list.find((c) => c.id === contact.id)) return;
    setList([...list, contact]);
  };

  const handleRemoveContact = (
    id: number,
    list: Contact[],
    setList: (contacts: Contact[]) => void
  ) => {
    setList(list.filter((contact) => contact.id !== id));
  };

  const saveFamily = async () => {
    try {
      const user = useAuthStore.getState().user;
      if (!user) throw new Error("Missing authenticated user");

      const familyData = {
        name: familyName,
        members: [
          ...contributors.map((c) => ({
            contact_id: c.id,
            family_membership_type: FamilyMembershipType.Contributor,
          })),
          ...dependents.map((c) => ({
            contact_id: c.id,
            family_membership_type: FamilyMembershipType.Dependant,
          })),
        ],
      };

     
      const lastFamily = await createFamilyWithMembers(familyData);

      setViewedType("FAMILY");
      setId(lastFamily.id);
      onClose();
    } catch (error: any) {
      const errorKey = error.response?.data?.detail?.error_key;

      switch (errorKey) {
        case "family_name_already_exists":
          setErrorMessage("שם המשפחה כבר קיים. אנא בחר שם אחר.");
          break;
        case "integrity_error":
          setErrorMessage("שגיאת תקינות במסד הנתונים.");
          break;
        default:
          setErrorMessage("אירעה שגיאה בלתי צפויה.");
          break;
      }
    }
  };

  const [searchQuery, setSearchQuery] = useState<string>("");

  const filterContacts = (contacts: Contact[], query: string): Contact[] => {
    if (!query) return contacts;
    const lower = query.toLowerCase();
    return contacts.filter((c) => {
      const fields = [
        formatName(c).toLowerCase(),
        c.phone?.toLowerCase() || "",
        c.email?.toLowerCase() || "",
        c.israeli_id || "",
      ];
      return fields.some((field) => field.includes(lower));
    });
  };

  const renderContactList = (
    list: Contact[],
    setList: (contacts: Contact[]) => void,
    toRender: Contact[]
  ) => {
    if (isLoading) return <CircularProgress />;
    if (error) return <Typography color="error">שגיאה בטעינה</Typography>;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, maxHeight: "50vh", overflowY: "auto" }}>
        {toRender.map((c) => (
          <Box
            key={c.id}
            onClick={() => handleContactSelect(c, list, setList)}
            sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", p: 1, backgroundColor: "#f5f5f5", "&:hover": { backgroundColor: "#e0e0e0" }, borderRadius: "8px" }}
          >
            <Avatar sx={{ backgroundColor: "#19E6d2" }}>{formatName(c).charAt(0).toUpperCase()}</Avatar>
           <Box>
            <Typography variant="body1">{formatName(c)}</Typography>
            {c.israeli_id && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                 {c.israeli_id}
              </Typography>
            )}
          </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderChips = (list: Contact[], setList: (contacts: Contact[]) => void) => (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {list.map((c) => (
        <Chip
          key={c.id}
          label={formatName(c)}
          onDelete={() => handleRemoveContact(c.id!, list, setList)}
          sx={{ backgroundColor: "#1976d2", color: "white", fontWeight: "bold", "& .MuiChip-deleteIcon": { color: "white" } }}
        />
      ))}
    </Box>
  );

  const renderStage = () => {
    const filteredContacts = contacts ? filterContacts(contacts, searchQuery) : [];

    if (stage === "contributors" || stage === "dependents") {
      const list = stage === "contributors" ? contributors : dependents;
      const setList = stage === "contributors" ? setContributors : setDependents;
      return (
        <>
          <TextField
            placeholder="חפש איש קשר..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ backgroundColor: "#f5f5f5", borderRadius: "8px" }}
          />
          {renderChips(list, setList)}
          {filteredContacts.length > 0 ? (
            renderContactList(list, setList, filteredContacts)
          ) : (
            <Typography sx={{ mt: 2 }}>לא נמצאו אנשי קשר</Typography>
          )}
          {stage === "contributors" && contributors.length === 2 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button onClick={() => setStage(SHOW_DEPENDENTS_STAGE ? "dependents" : "family")} sx={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#45a049" } }}>
                <ArrowForwardIcon sx={{ color: "white" }} />
              </Button>
            </Box>
          )}
          {stage === "dependents" && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button onClick={() => setStage("family")} sx={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#45a049" } }}>
                <ArrowForwardIcon sx={{ color: "white" }} />
              </Button>
            </Box>
          )}
        </>
      );
    }

    if (stage === "family") {
      return (
        <>
          <Avatar sx={{ width: 80, height: 80, backgroundColor: "#e0e0e0", mx: "auto", mt: 2 }}>
            <PeopleIcon sx={{ fontSize: 40, color: "#bdbdbd" }} />
          </Avatar>
          <TextField
            fullWidth
            placeholder="שם משפחה"
            value={familyName}
            onChange={(e) => {
              setFamilyName(e.target.value);
              setErrorMessage(null);
            }}
            error={!!errorMessage}
            helperText={errorMessage}
            sx={{ mt: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button onClick={saveFamily} sx={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#45a049" } }}>
              <ArrowForwardIcon sx={{ color: "white" }} />
            </Button>
          </Box>
        </>
      );
    }
  };

  return (
    <Box sx={{ p: 2, height: "100%", position: "relative" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ position: "absolute", top: 16, right: 16 }}>
        {stage === "contributors" ? "הוסף תורמים" : stage === "dependents" ? "הוסף תלויים" : "משפחה חדשה"}
      </Typography>
      <IconButton
        onClick={
          stage === "contributors"
            ? onClose
            : () => setStage(stage === "family" && SHOW_DEPENDENTS_STAGE ? "dependents" : "contributors")
        }
        sx={{ position: "absolute", top: 16, left: 16 }}
      >
        {stage === "contributors" ? <CloseIcon /> : <ArrowBackIcon />}
      </IconButton>
      <Box sx={{ marginTop: 6 }}>{renderStage()}</Box>    </Box>
  );
};
